"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import ERPLeadBot from "./ERPLeadBot";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ArrowRight, Search, Loader2, Zap } from "lucide-react";
import Footer from "./Footer";
import { API_ENDPOINTS } from "@/constants/config";

const CATEGORIES = ["All", "Technology", "Safety", "Finance", "Education", "Communication", "General"];

const CATEGORY_COLORS = {
  Technology: "bg-blue-600",
  Safety: "bg-green-600",
  Finance: "bg-amber-600",
  Education: "bg-purple-600",
  Communication: "bg-pink-600",
  General: "bg-gray-600",
  "Remote Work": "bg-orange-500",
  Analytics: "bg-violet-600",
  Methodology: "bg-teal-600",
  Automation: "bg-cyan-600",
};

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const AVATAR_PALETTE = ["#4F46E5", "#7C3AED", "#DB2777", "#DC2626", "#D97706", "#059669", "#0891B2"];
const avatarColor = (name) => {
  if (!name) return AVATAR_PALETTE[0];
  return AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];
};
const initials = (name) => {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
};

function AuthorAvatar({ name, size = 8 }) {
  return (
    <div
      className={`w-${size} h-${size} rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0`}
      style={{ backgroundColor: avatarColor(name) }}
    >
      {initials(name)}
    </div>
  );
}

function CategoryBadge({ category }) {
  const color = CATEGORY_COLORS[category] || "bg-gray-600";
  return (
    <span className={`${color} text-white text-xs font-medium px-2.5 py-1 rounded-full`}>
      {category}
    </span>
  );
}

export default function Blog() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [email, setEmail] = useState("");
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.publicBlogs}?limit=50`);
        const json = await res.json();
        if (json?.data?.blogs) setBlogs(json.data.blogs);
      } catch {
        // silently fail — show empty state
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const handleCardOpen = (slug) => navigate(`/blog/${slug}`);
  const handleCardKeyDown = (event, slug) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardOpen(slug);
    }
  };

  const filteredPosts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return blogs.filter((post) => {
      const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
      if (!normalizedQuery) return matchesCategory;
      const searchableText = `${post.title} ${post.excerpt} ${post.category} ${(post.tags || []).join(" ")}`.toLowerCase();
      return matchesCategory && searchableText.includes(normalizedQuery);
    });
  }, [blogs, searchQuery, selectedCategory]);

  const featuredPosts = filteredPosts.filter((post) => post.isFeatured);
  const recentPosts = filteredPosts.filter((post) => !post.isFeatured);

  const activeCategories = useMemo(() => {
    const used = new Set(blogs.map((b) => b.category));
    return CATEGORIES.filter((c) => c === "All" || used.has(c));
  }, [blogs]);

  return (
    <div className="w-full bg-white">
      {/* ── Header ─────────────────────────────────────────── */}
      <section className="py-10 border-b bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Clynox <span className="text-blue-600">Blog</span>
              </h1>
              <p className="text-gray-500 mt-1 text-sm">
                Insights, tips, and best practices for modern management teams.
              </p>
            </div>
            <div className="relative w-full sm:w-72 flex-shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10 rounded-full border-gray-200 bg-gray-50 focus:bg-white"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {activeCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Content ────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <>
          {/* Featured Article */}
          {featuredPosts.length > 0 && (
            <section className="py-12 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-2 mb-6">
                  <Zap className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-semibold uppercase tracking-widest text-blue-600">
                    Featured Article
                  </span>
                </div>
                {featuredPosts.map((post) => (
                  <div
                    key={post._id}
                    className="grid md:grid-cols-2 rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleCardOpen(post.slug)}
                    onKeyDown={(e) => handleCardKeyDown(e, post.slug)}
                  >
                    <div className="relative aspect-[4/3] md:aspect-auto">
                      <img
                        src={post.coverImage || "/everythingknow.png"}
                        alt={post.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute top-4 left-4">
                        <CategoryBadge category={post.category} />
                      </div>
                    </div>
                    <div className="p-8 md:p-10 flex flex-col justify-center bg-white">
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight">
                        {post.title}
                      </h2>
                      <p className="text-gray-500 mb-6 leading-relaxed line-clamp-3">{post.excerpt}</p>
                      <div className="flex items-center gap-4 mb-6">
                        {post.author && (
                          <div className="flex items-center gap-2">
                            <AuthorAvatar name={post.author} />
                            <span className="text-sm font-medium text-gray-700">{post.author}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-sm text-gray-400">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{formatDate(post.publishedAt)}</span>
                        </div>
                        {post.readTime && (
                          <div className="flex items-center gap-1 text-sm text-gray-400">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{post.readTime}</span>
                          </div>
                        )}
                      </div>
                      <Button className="w-fit group bg-blue-600 hover:bg-blue-700" tabIndex={-1}>
                        Read Article
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Article Grid */}
          <section className={`py-12 ${featuredPosts.length > 0 ? "bg-gray-50" : "bg-white"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-6">
                {featuredPosts.length > 0 ? "Latest Articles" : "All Articles"}
              </h2>

              {recentPosts.length === 0 && featuredPosts.length === 0 ? (
                <div className="py-24 text-center text-gray-400">
                  {searchQuery || selectedCategory !== "All"
                    ? "No articles match your filters."
                    : "No articles published yet. Check back soon!"}
                </div>
              ) : recentPosts.length === 0 ? (
                <div className="py-16 text-center text-gray-400">
                  No recent articles match your filters.
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {recentPosts.map((post) => (
                    <div
                      key={post._id}
                      className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex flex-col"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleCardOpen(post.slug)}
                      onKeyDown={(e) => handleCardKeyDown(e, post.slug)}
                    >
                      <div className="relative aspect-video flex-shrink-0">
                        <img
                          src={post.coverImage || "/everythingknow.png"}
                          alt={post.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                        <div className="absolute top-3 left-3">
                          <CategoryBadge category={post.category} />
                        </div>
                        {post.readTime && (
                          <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                            <Clock className="w-3 h-3" />
                            <span>{post.readTime}</span>
                          </div>
                        )}
                      </div>

                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-2">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{formatDate(post.publishedAt)}</span>
                        </div>
                        <h3 className="text-base font-bold text-gray-900 line-clamp-2 mb-2 leading-snug">
                          {post.title}
                        </h3>
                        <p className="text-sm text-gray-500 line-clamp-3 flex-1">{post.excerpt}</p>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                          {post.author ? (
                            <div className="flex items-center gap-2">
                              <AuthorAvatar name={post.author} size={7} />
                              <span className="text-sm font-medium text-gray-700">{post.author}</span>
                            </div>
                          ) : (
                            <span />
                          )}
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* ── Newsletter ─────────────────────────────────────── */}
      <section className="py-16 bg-gradient-to-br from-blue-700 to-blue-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-3 justify-center">
            <Zap className="w-4 h-4 text-blue-300" />
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-300">
              Stay Updated
            </span>
          </div>
          <h2 className="text-3xl font-bold text-white text-center mb-3">Never Miss an Insight</h2>
          <p className="text-blue-200 text-center mb-8">
            Get the latest management strategies and software insights delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 h-11 bg-white/10 border-white/20 text-white placeholder:text-blue-300 focus:bg-white/20"
            />
            <Button size="lg" className="bg-white text-blue-700 hover:bg-gray-100 font-semibold px-6 h-11">
              Subscribe Now
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ERPLeadBot />
    </div>
  );
}
